import React from 'react'
import { FormattedMessage } from 'react-intl'

import { differenceInCalendarDays, parseISO } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { AttentionIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AttentionIcon'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { OVERDUE_TASK_THRESHOLD_DAYS } from '../constants'

export const getDaysOld = (date: string): number => differenceInCalendarDays(new Date(), parseISO(date))

const Row = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

type DaysOldLabelProps = {
  daysOld: number
  variant?: 'daysOld' | 'dueDaysAgo'
  size?: BodyTextSize
}

export const DaysOldLabel: React.FC<DaysOldLabelProps> = ({
  daysOld,
  variant = 'daysOld',
  size = BodyTextSize.CAPTION,
}) => {
  const { colors } = useTheme()
  const isOverdue = daysOld >= OVERDUE_TASK_THRESHOLD_DAYS
  const isDueDaysAgo = variant === 'dueDaysAgo'

  return (
    <Row>
      <BodyText
        size={size}
        color={isOverdue ? colors.textWarning : colors.textSecondary}
        text={
          daysOld === 0 ? (
            <FormattedMessage defaultMessage='From today' description='Days old label' />
          ) : isDueDaysAgo ? (
            <FormattedMessage
              defaultMessage='{days, plural, one {Due # day ago} other {Due # days ago}}'
              description='Days old label, phrased as a due date'
              values={{ days: daysOld }}
            />
          ) : (
            <FormattedMessage
              defaultMessage='{days, plural, one {# day old} other {# days old}}'
              description='Days old label'
              values={{ days: daysOld }}
            />
          )
        }
      />
      {isOverdue && !isDueDaysAgo && <AttentionIcon size={18} testID={tID('DaysOldLabel-overdue-icon')} />}
    </Row>
  )
}
